import localhost from "apis/localhost";
import { URLENCODED_FORM_CONTENT_TYPE } from "constants/app";
import { READABILITY_CHUNK_SIZE } from "constants/readability";
import { highlightIssueRegions } from "layout/documentReadabilityReport/renderers";
import { showErrorModal } from "slices/app/actions/error";
import { pageLoading } from "slices/app/appSlice";
import { setParagraphs, setSummary } from "slices/readability/readabilitySlice";

export const getSummaryBasics = (folderId, onSuccess) => async (dispatch) => {
  dispatch(pageLoading(true));
  await localhost
    .get(`/document/readability/summary/${folderId}`)
    .then(async (response) => {
      const { data } = response;
      dispatch(setSummary(data));
      if (onSuccess) {
        onSuccess(data);
      }
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(pageLoading(false));
    });
};

export const getSummary =
  (folderId, documentId, onSuccess) => async (dispatch) => {
    dispatch(pageLoading(true));
    await localhost
      .get(`/document/readability/summary/${folderId}/${documentId}`)
      .then(async (response) => {
        const { data } = response;
        dispatch(setSummary(data));
        onSuccess(data);
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const getParagraphs =
  (
    documentId,
    offset,
    lastLoadedBlock,
    language,
    headingMapper,
    onLoad,
    paragraphs
  ) =>
  async (dispatch, getState) => {
    dispatch(pageLoading(true));
    let realOffset = offset;
    let fromIndex = 0;
    if (
      lastLoadedBlock !== undefined &&
      paragraphs[(lastLoadedBlock + 1) * READABILITY_CHUNK_SIZE - 1]
    ) {
      realOffset =
        offset -
        (lastLoadedBlock !== undefined
          ? (lastLoadedBlock + 1) * READABILITY_CHUNK_SIZE
          : 0);
      if (realOffset > 0) {
        fromIndex =
          lastLoadedBlock !== undefined
            ? paragraphs[(lastLoadedBlock + 1) * READABILITY_CHUNK_SIZE - 1]
                .index
            : 0;
      } else {
        realOffset = offset;
      }
    }
    await localhost
      .get(
        `/document/readability/paragraphs/${documentId}?offset=${realOffset}&pageSize=${READABILITY_CHUNK_SIZE}&language=${language}&fromIndex=${fromIndex}`
      )
      .then((response) => {
        if (onLoad) {
          onLoad();
        }
        const { data } = response;
        let index = offset + 1;
        let mapperPrimed = false; // needed when randomly jumping around paged result chunks of paragraphs
        const prepareResultData = (d) => {
          d.forEach((i) => {
            i.id = "" + index;
            i.stats = JSON.parse(i.stats);
            i.hits = Object.keys(i.issues)
              .flatMap((k) =>
                i.issues[k].hits.map((hit) => ({ ...hit, issue: i.issues[k] }))
              )
              .sort((a, b) => a.start - b.start);
            i.highlightedText = highlightIssueRegions(i.stats.text, i.hits);
            if (headingMapper) {
              if (mapperPrimed) {
                i.heading = headingMapper.from(i.index);
              } else {
                i.heading = headingMapper.at(i.index);
                mapperPrimed = true;
              }
            }
            index++;
          });
        };
        let paragraphsOverride = getState().readability?.paragraphs;
        if (paragraphsOverride === undefined) {
          paragraphsOverride = paragraphs;
        }
        prepareResultData(data);
        const copy = [...paragraphsOverride];
        copy.splice(offset, data.length, ...data);
        dispatch(setParagraphs(copy));
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const folderReadabilityPDFReport =
  ({ folderId, folderName }) =>
  async (dispatch) => {
    dispatch(pageLoading(true));
    localhost
      .post(
        "/document/readability/export/pdf/folder",
        {
          docSetId: folderId,
          docSet: folderName,
          reportType: "document-clearlanguage-folderview",
        },
        {
          headers: {
            "Content-Type": URLENCODED_FORM_CONTENT_TYPE,
          },
        }
      )
      .then((response) => {
        const { data } = response;
        window.location = data;
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const documentReadabilityPDFReport =
  ({ folderId, documentId, documentName, metricsToShow }) =>
  async (dispatch) => {
    dispatch(pageLoading(true));
    localhost
      .post(
        "/document/readability/export/pdf/document",
        {
          docSetId: folderId,
          documentId,
          docRef: documentName,
          metricsToShow,
          reportType: "document-plainlanguage",
        },
        {
          headers: {
            "Content-Type": URLENCODED_FORM_CONTENT_TYPE,
          },
        }
      )
      .then((response) => {
        const { data } = response;
        window.location = data;
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const documentReadabilityExcelReport = ({
  folderId,
  documentId,
  documentName,
  metricsToShow,
}) =>
  localhost.post(
    "/report",
    {
      report: "document-plainlanguageExcel",
      reportType: "plainlanguageExcel",
      docSetId: folderId,
      documentId,
      docRef: documentName,
      metricsToShow,
    },
    {
      headers: {
        "Content-Type": URLENCODED_FORM_CONTENT_TYPE,
      },
    }
  );

export const statusDocumentReadabilityExcelReport = (jobId) =>
  localhost.get(`/jobStatus?jobId=${jobId}`);

export const documentReadabilityAnnotatedDocument = ({
  folderId,
  documentId,
  documentName,
  metricsToShow,
}) => {
  return localhost.post(
    "/report",
    {
      reportType: "annotatedDocxClearLanguage",
      folderId,
      docId: documentId,
      docRef: documentName,
      metricsToShow,
    },
    {
      headers: {
        "Content-Type": URLENCODED_FORM_CONTENT_TYPE,
      },
    }
  );
};

export const statusDocumentReadabilityAnnotatedDocument = (jobId) =>
  localhost.get(`/jobStatus?jobId=${jobId}`);
